import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { setUser as setSentryUser, clearUser as clearSentryUser } from '../utils/sentryHelpers';

// إنشاء سياق المصادقة
const AuthContext = createContext(null);

// إعداد الدالة المساعدة لوصول API
axios.defaults.baseURL = API_BASE_URL;

// إنشاء axios instance منفصل لتجنب التداخل
const createAuthAxios = () => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000, // 10 seconds timeout
  });

  // إضافة interceptor للتعامل مع انتهاء صلاحية التوكن
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // تحقق من أن الخطأ ليس من endpoint تسجيل الدخول
        const url = error.config?.url || '';
        const isLoginError = url.includes('/api/login');

        // فقط قم بإعادة التوجيه إذا لم يكن الخطأ من تسجيل الدخول
        // (لأن 401 من /api/login يعني أن بيانات الدخول خاطئة وليس انتهاء صلاحية التوكن)
        if (!isLoginError) {
          // إزالة التوكن المنتهي الصلاحية
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          // إعادة تحميل الصفحة للعودة إلى صفحة تسجيل الدخول
          window.location.href = '/';
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // التحقق من الجلسة عند تحميل التطبيق مع دعم إلغاء الطلبات
  useEffect(() => {
    const abortController = new AbortController();

    const checkSession = async () => {
      const token = localStorage.getItem('token');

      if (token) {
        try {
          // إعداد التوكن في رأس الطلب
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          // طلب بيانات المستخدم مع إمكانية الإلغاء
          const response = await axios.get('/api/users/me', {
            signal: abortController.signal
          });
          setUser(response.data);
          // Track user in Sentry
          setSentryUser({
            id: response.data.id,
            email: response.data.email,
            username: response.data.username || response.data.name
          });
        } catch (err) {
          // تجاهل الأخطاء إذا تم إلغاء الطلب
          if (err.name === 'AbortError' || err.code === 'ERR_CANCELED' || axios.isCancel(err)) {
            return;
          }

          // فقط قم بإزالة التوكن إذا كان الخطأ 401 (غير مصرح به)
          if (err.response && err.response.status === 401) {
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            setError('انتهت صلاحية الجلسة، يرجى تسجيل الدخول مرة أخرى');
          } else {
            // أخطاء الشبكة أو الخادم (مثل 500 أو عدم الاتصال)
            setError('حدث خطأ في الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت');
          }
        }
      }

      setLoading(false);
    };

    checkSession();

    // تنظيف عند إلغاء تحميل المكون
    return () => {
      abortController.abort();
    };
  }, []);

  // دالة تسجيل الدخول مع تحسين الأداء ودعم إلغاء الطلبات
  const login = useCallback(async (email, password, recaptchaToken, signal) => {
    const authAxios = createAuthAxios();

    try {
      setLoading(true);
      setError(null); // مسح الأخطاء السابقة

      const response = await authAxios.post('/api/login', {
        email,
        password,
        recaptcha_token: recaptchaToken
      }, {
        signal: signal
      });

      // حفظ التوكن وإعداده للطلبات المستقبلية
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      authAxios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // طلب بيانات المستخدم مع إمكانية الإلغاء
      const userResponse = await authAxios.get('/api/users/me', {
        signal: signal
      });
      setUser(userResponse.data);
      // Track user in Sentry
      setSentryUser({
        id: userResponse.data.id,
        email: userResponse.data.email,
        username: userResponse.data.username || userResponse.data.name
      });

      return { success: true };
    } catch (err) {
      // تجاهل الأخطاء إذا تم إلغاء الطلب
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return { success: false, cancelled: true };
      }

      const errorMessage = err.response?.data?.detail || 'حدث خطأ أثناء تسجيل الدخول';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // التحقق من توفر البريد الإلكتروني
  const checkEmailAvailability = async (email, recaptchaToken) => {
    try {
      const response = await axios.post('/api/auth/check-email', {
        email,
        recaptcha_token: recaptchaToken
      });
      return {
        success: true,
        available: response.data.available,
        message: response.data.message
      };
    } catch (err) {
      return {
        success: false,
        available: false,
        error: err.response?.data?.detail || 'حدث خطأ أثناء التحقق من البريد الإلكتروني'
      };
    }
  };

  // دالة التسجيل
  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await axios.post('/api/register', userData);

      // استخراج التوكن من استجابة التسجيل مباشرة
      const { access_token, ...userInfo } = response.data;

      // حفظ التوكن وإعداده للطلبات المستقبلية
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // تحديث حالة المستخدم
      setUser(userInfo);
      setError(null);
      // Track user in Sentry
      setSentryUser({
        id: userInfo.id,
        email: userInfo.email,
        username: userInfo.username || userInfo.name
      });

      return { success: true, user: userInfo };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'حدث خطأ أثناء التسجيل';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // دالة تسجيل الخروج مع تحسين الأداء
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setError(null); // مسح الأخطاء عند تسجيل الخروج
    // Clear user from Sentry
    clearSentryUser();
  }, []);

  // تحديث بيانات المستخدم
  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // إعداد التوكن في رأس الطلب
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // طلب بيانات المستخدم المحدثة
        const response = await axios.get('/api/users/me');
        setUser(response.data);
        return { success: true };
      }
      return { success: false, error: 'لا يوجد توكن' };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'حدث خطأ أثناء تحديث بيانات المستخدم';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // تحديث التوكن عند تغيير البريد الإلكتروني
  const updateToken = (newToken) => {
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  // إنشاء قيم السياق مع تحسين الأداء
  const contextValue = useMemo(() => ({
    user,
    loading,
    error,
    login,
    register,
    logout,
    checkEmailAvailability,
    setError,
    refreshUser,
    updateToken,
    token: localStorage.getItem('token')
  }), [user, loading, error, login, register, logout, refreshUser, updateToken]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// دالة Hook لسهولة استخدام سياق المصادقة
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};